import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Web as WebIcon
} from '@mui/icons-material';
import { PageDefinition } from '../../types/pages';
import ComponentRenderer from './ComponentRenderer';

interface PagePreviewProps {
  page: PageDefinition;
  onBack: () => void;
  onEdit: () => void;
}

const PagePreview: React.FC<PagePreviewProps> = ({ page, onBack, onEdit }) => {
  const renderSectionWidth = (width: string) => {
    switch (width) {
      case 'full': return 12;
      case 'half': return 6;
      case 'third': return 4;
      case 'quarter': return 3;
      case 'two-thirds': return 8;
      case 'three-quarters': return 9;
      default: return 12;
    }
  };

  const renderLayoutPreview = () => {
    const { layout } = page;
    
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Layout Preview ({layout.type})
        </Typography>
        
        <Grid container spacing={2}>
          {layout.sections.map((section, index) => (
            <Grid size={renderSectionWidth(section.width)} key={section.id}>
              <Card variant="outlined" sx={{ minHeight: 100 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    Section {index + 1}: {section.name || 'Untitled Section'}
                  </Typography>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Width: {section.width} • {section.components.length} component(s)
                  </Typography>
                  
                  {/* Render section components */}
                  {section.components.length > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      {section.components
                        .sort((a, b) => a.order - b.order)
                        .map((component) => (
                          <Box key={component.id} sx={{ mb: 2, last: { mb: 0 } }}>
                            <ComponentRenderer component={component} />
                          </Box>
                        ))}
                    </Box>
                  ) : (
                    <Box 
                      sx={{ 
                        textAlign: 'center', 
                        py: 3, 
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '2px dashed',
                        borderColor: 'grey.300'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No components added to this section
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<BackIcon />}
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            Back to Pages
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {page.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Preview of "{page.name}"
            </Typography>
          </Box>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={onEdit}
        >
          Edit Page
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Page Header */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <WebIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {page.title}
              </Typography>
            </Box>
            
            {page.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {page.description}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={page.category} color="primary" size="small" />
              <Chip label={page.route} variant="outlined" size="small" />
              <Chip 
                label={page.isActive ? 'Published' : 'Draft'} 
                color={page.isActive ? 'success' : 'default'} 
                size="small" 
              />
              {page.isSystem && <Chip label="System Page" color="info" size="small" />}
            </Box>
          </Paper>

          {/* Layout Preview */}
          {renderLayoutPreview()}

          {/* Forms Section */}
          {page.forms && page.forms.length > 0 && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Forms ({page.forms.length})
              </Typography>
              <Grid container spacing={2}>
                {page.forms.map((form) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={form.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2">
                          Form: {form.formId}
                        </Typography>
                        {form.title && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {form.title}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label={`Order: ${form.order}`} size="small" />
                          {form.required && <Chip label="Required" color="error" size="small" />}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Components Section */}
          {page.components && page.components.length > 0 && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Components ({page.components.length})
              </Typography>
              <Grid container spacing={2}>
                {page.components.map((component) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={component.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2">
                          Component: {component.componentId}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip label={`Order: ${component.order}`} size="small" />
                          {component.sectionId && (
                            <Chip label={`Section: ${component.sectionId}`} size="small" />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Page Information */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Page Information</Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Route</Typography>
              <Typography variant="body2" color="text.secondary">
                {page.route}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Version</Typography>
              <Typography variant="body2" color="text.secondary">
                {page.version}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Created</Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(page.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Last Updated</Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(page.updatedAt).toLocaleDateString()}
              </Typography>
            </Box>
          </Paper>

          {/* Permissions */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Permissions</Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Can View</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {page.permissions.view.map((role) => (
                  <Chip key={role} label={role} color="primary" size="small" />
                ))}
              </Box>
            </Box>
            
            {page.permissions.edit && page.permissions.edit.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Can Edit</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {page.permissions.edit.map((role) => (
                    <Chip key={role} label={role} color="secondary" size="small" />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>

          {/* SEO Meta */}
          {(page.meta?.pageTitle || page.meta?.pageDescription) && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>SEO & Meta</Typography>
              
              {page.meta.pageTitle && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">HTML Page Title</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {page.meta.pageTitle}
                  </Typography>
                </Box>
              )}
              
              {page.meta.pageDescription && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Meta Description</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {page.meta.pageDescription}
                  </Typography>
                </Box>
              )}
              
              {page.meta.keywords && page.meta.keywords.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Keywords</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {page.meta.keywords.map((keyword, index) => (
                      <Chip key={index} label={keyword} variant="outlined" size="small" />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          )}

          {/* Styling */}
          {page.styling && Object.keys(page.styling).length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Styling</Typography>
              
              {page.styling.theme && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Theme</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {page.styling.theme}
                  </Typography>
                </Box>
              )}
              
              {page.styling.primaryColor && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Primary Color</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: page.styling.primaryColor,
                        border: '1px solid #ccc',
                        borderRadius: 1
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {page.styling.primaryColor}
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {page.styling.backgroundColor && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Background Color</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: page.styling.backgroundColor,
                        border: '1px solid #ccc',
                        borderRadius: 1
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {page.styling.backgroundColor}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PagePreview;